import { CancelActivityService } from '../services/cancel-activity.service';
import { carriesAValue } from '../../../utils/an-answer-that-carries-its-value';
import { Injectable } from '@angular/core';
import { Action, Actions, ofActionSuccessful, State, StateContext, Store } from '@ngxs/store';
import {
  DeleteMyEventsAction,
  LoadMyCancelEventsAction,
  LoadMyNextEventsAction,
  LoadMyPastEventsAction,
} from './my-events.actions';
import { tap } from 'rxjs';
import { ActiveEventDetailOutputDto, EventsService } from '@smatchy/sdk';
import { CreateEventAction } from './create-event.actions';
import { LocationAccessService } from '../../user/services/location-access.service';

export interface MyEventsStateModel {
  loading: boolean;
  nextEvents: ActiveEventDetailOutputDto[];
  pastEvents: ActiveEventDetailOutputDto[];
  cancelEvents: ActiveEventDetailOutputDto[];
}

const defaults: MyEventsStateModel = {
  loading: false,
  nextEvents: [],
  pastEvents: [],
  cancelEvents: [],
};

@State<MyEventsStateModel>({
  name: 'myEvents',
  defaults,
})
@Injectable()
export class MyEventsState {
  /**
   * The last request of each list. Only its answer may write the list.
   *
   * WHY. A list asked before a cancellation is answered can come back after
   * it, still saying the outing is planned, and put it back where the
   * cancellation had just taken it out. The API writes the cancelled status
   * last, after the refunds and the messages to the people registered, so a
   * list asked during those seconds is stale by construction. Seen on
   * 22 October 2026 (Asana 1219817831444226): a cancelled outing stayed under
   * « En cours » until the app was reloaded.
   *
   * Counted from the moment the action starts, before the position is read,
   * so a newer request outranks an older one even while it waits for the GPS.
   */
  private readonly latestRequest = { next: 0, past: 0, cancel: 0 };

  constructor(
    private readonly cancelActivityService: CancelActivityService,
    private readonly eventsService: EventsService,
    private readonly store: Store,
    private readonly actions: Actions,
    private readonly locationAccess: LocationAccessService,
  ) {
    this.actions.pipe(ofActionSuccessful(CreateEventAction)).subscribe((_) => {
      this.store.dispatch(new LoadMyNextEventsAction());
    });
  }

  @Action(LoadMyNextEventsAction)
  async loadMyNextEventsAction({ patchState }: StateContext<MyEventsStateModel>) {
    const request = ++this.latestRequest.next;
    patchState({
      loading: true,
    });
    const reference = await this.locationAccess.resolveEventReferenceLocation();
    const requestParams = reference ? { lat: reference.lat, lon: reference.lon } : {};
    return this.eventsService.getMyNextEventsSubscribed(requestParams).pipe(
      tap((value) => {
        if (!carriesAValue(value)) {
          console.error(value.error);
          return;
        }
        if (request !== this.latestRequest.next) {
          return;
        }
        patchState({
          loading: false,
          nextEvents: value.value.events,
        });
      }),
    );
  }

  @Action(LoadMyPastEventsAction)
  async loadMyPastEventsAction({ patchState }: StateContext<MyEventsStateModel>) {
    const request = ++this.latestRequest.past;
    patchState({
      loading: true,
    });
    const reference = await this.locationAccess.resolveEventReferenceLocation();
    const requestParams = reference ? { lat: reference.lat, lon: reference.lon } : {};
    return this.eventsService.getMyFinishedEvent(requestParams).pipe(
      tap((value) => {
        if (!carriesAValue(value)) {
          console.error(value.error);
          return;
        }
        if (request !== this.latestRequest.past) {
          return;
        }
        patchState({
          loading: false,
          pastEvents: value.value.events,
        });
      }),
    );
  }

  @Action(LoadMyCancelEventsAction)
  async loadMyCancelEventsAction({ patchState }: StateContext<MyEventsStateModel>) {
    const request = ++this.latestRequest.cancel;
    patchState({
      loading: true,
    });
    const reference = await this.locationAccess.resolveEventReferenceLocation();
    const requestParams = reference ? { lat: reference.lat, lon: reference.lon } : {};
    return this.eventsService.getMyCancelEvents(requestParams).pipe(
      tap((value) => {
        if (!carriesAValue(value)) {
          console.error(value.error);
          return;
        }
        if (request !== this.latestRequest.cancel) {
          return;
        }
        patchState({
          loading: false,
          cancelEvents: value.value.events,
        });
      }),
    );
  }

  @Action(DeleteMyEventsAction)
  async deleteMyEventsAction(
    { patchState, getState, dispatch }: StateContext<MyEventsStateModel>,
    { eventId, reason, reasonCustomText }: DeleteMyEventsAction,
  ) {
    patchState({
      loading: true,
    });
    // SMA-100: the generated client takes an id and nothing else, so the
    // cancellation goes through the hand-written service, which carries the
    // reason and the word left for the people registered.
    return this.cancelActivityService.cancel(eventId, reason, reasonCustomText).pipe(
      // No annotation on the parameter: the service says what it answers, and
      // an annotation here only stopped the guard below from narrowing it.
      tap((value) => {
        if (!carriesAValue(value)) {
          console.error(value.error);
          patchState({
            loading: false,
          });
          // Thrown so the page that asked does not announce a cancellation
          // that did not happen.
          throw new Error('The activity could not be cancelled');
        }
        patchState({
          loading: false,
          nextEvents: getState().nextEvents.filter((event) => event.id !== eventId),
          pastEvents: getState().pastEvents.filter((event) => event.id !== eventId),
        });
        // The three lists, and so the three counts of « Mes activités », are
        // asked again: the outing moves to « Annulé », and a list asked while
        // the cancellation was running is now outranked. Not waited for, so
        // the cancellation does not also wait for the position.
        dispatch([new LoadMyNextEventsAction(), new LoadMyPastEventsAction(), new LoadMyCancelEventsAction()]);
      }),
    );
  }
}
